import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  signInWithEmail, 
  signUpWithEmail, 
  signInWithGoogle,
  handleGoogleRedirectResult,
  logout as authLogout,
  onAuthStateChange,
  updateUserProfile
} from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    
    // Check if we have a session in localStorage (for immediate loading)
    const cachedUser = localStorage.getItem('lastAuthUser');
    if (cachedUser) {
      try {
        const parsedUser = JSON.parse(cachedUser);
        setUser(parsedUser);
        // Keep loading true until Firebase confirms
      } catch (err) {
        console.error('Error parsing cached user:', err);
      }
    }

    // Check for Google redirect result first
    const checkRedirectResult = async () => {
      try {
        const result = await handleGoogleRedirectResult();
        if (result.success && result.user && mounted) {
          setUser(result.user);
          localStorage.setItem('lastAuthUser', JSON.stringify(result.user));
          // Trigger redirect to appropriate dashboard
          window.dispatchEvent(new CustomEvent('google-signin-complete', { detail: result.user }));
        }
      } catch (err) {
        console.error('Redirect result error:', err);
      }
    };

    checkRedirectResult();

    // Listen to Firebase auth state changes
    const unsubscribe = onAuthStateChange((firebaseUser) => {
      if (mounted) {
        setUser(firebaseUser);
        if (firebaseUser) {
          // Cache user for instant loading on next page load
          localStorage.setItem('lastAuthUser', JSON.stringify(firebaseUser));
        } else {
          // Clear cache if no user
          localStorage.removeItem('lastAuthUser');
        }
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  const login = async (email, password) => {
    try {
      setError(null);
      setLoading(true);
      const result = await signInWithEmail(email, password);
      
      if (result.success) {
        setUser(result.user);
        return result.user;
      } else {
        setError(result.error);
        throw new Error(result.error);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const loginWithGoogle = async () => {
    try {
      setError(null);
      setLoading(true);
      const result = await signInWithGoogle();
      
      if (result.success) {
        setUser(result.user);
        return result.user;
      } else {
        setError(result.error);
        throw new Error(result.error);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signup = async (userData) => {
    try {
      setError(null);
      setLoading(true);
      const { email, password, ...profileData } = userData;
      
      const result = await signUpWithEmail(email, password, profileData);
      
      if (result.success) {
        setUser(result.user);
        return result.user;
      } else {
        setError(result.error);
        throw new Error(result.error);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setError(null);
      const result = await authLogout();
      
      if (result.success) {
        setUser(null);
        localStorage.removeItem('lastAuthUser'); // Clear cached user on logout
      } else {
        setError(result.error);
        throw new Error(result.error);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const updateUser = async (updates) => {
    try {
      setError(null);
      if (!user) throw new Error('No user logged in');
      
      const result = await updateUserProfile(user.uid, updates);
      
      if (result.success) {
        const updatedUser = { ...user, ...updates };
        setUser(updatedUser);
        return updatedUser;
      } else {
        setError(result.error);
        throw new Error(result.error);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    }
  };

  const checkOnboardingStatus = () => {
    if (!user) return false;
    return user.onboardingCompleted || false;
  };

  const favoriteCaregivers = Array.isArray(user?.favoriteCaregivers)
    ? user.favoriteCaregivers
    : [];

  const isFavorite = (caregiverId) => favoriteCaregivers.includes(caregiverId);

  const toggleFavorite = async (caregiverId) => {
    if (!user || !caregiverId) return false;

    const next = favoriteCaregivers.includes(caregiverId)
      ? favoriteCaregivers.filter((id) => id !== caregiverId)
      : [...favoriteCaregivers, caregiverId];

    await updateUser({ favoriteCaregivers: next });
    return next.includes(caregiverId);
  };

  const value = {
    user,
    login,
    loginWithGoogle,
    signup,
    logout,
    updateUser,
    checkOnboardingStatus,
    favoriteCaregivers,
    isFavorite,
    toggleFavorite,
    loading,
    error
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};